import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { LabApiError, labApi } from './api'
import { ComparisonVideo } from './comparison-video-ui'
import { PelicanMedia, Plate, ResultCard, ShareButtons } from './components'
import { formatCost, formatScore, modelLabel, modelSlug, resultPath, scenePath } from './format'
import type { SceneSlug } from './scenes'
import { useT } from './i18n'
import type { LabLeaderRow, LabResult } from './types'

// Ported from the earlier sandbase-fe Lab components (GuessGame, FaceOff, Explore).

function shuffle<T>(list: T[], rand: () => number): T[] {
  const out = [...list]
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

// ─── Guess the model ─────────────────────────────────────────

const ROUNDS = 5
export interface GuessRound { result: LabResult; options: string[] }

/** Builds rounds from distinct models; each round offers the right model and three others. */
export function buildGuessRounds(pool: LabResult[], rand: () => number = Math.random): GuessRound[] {
  const models = Array.from(new Set(pool.map((r) => r.model)))
  if (models.length < 4) return []
  const byModel = new Map<string, LabResult[]>()
  for (const r of pool) if (r.ok) byModel.set(r.model, [...(byModel.get(r.model) ?? []), r])
  return shuffle(Array.from(byModel.keys()), rand).slice(0, ROUNDS).map((model) => {
    const candidates = byModel.get(model)!
    const result = candidates[Math.floor(rand() * candidates.length)]
    const others = shuffle(models.filter((m) => m !== model), rand).slice(0, 3)
    return { result, options: shuffle([model, ...others], rand) }
  })
}

// Verdicts live in i18n (t.games.verdicts).

export function GuessGame({ pool, scene = 'pelican-test' }: { pool: LabResult[]; scene?: SceneSlug }) {
  void scene
  const t = useT()
  const g = t.games
  const VERDICTS = g.verdicts
  const [seed, setSeed] = useState(0)
  const rounds = useMemo(() => buildGuessRounds(pool), [pool, seed]) // eslint-disable-line react-hooks/exhaustive-deps
  const [index, setIndex] = useState(0)
  const [picked, setPicked] = useState<string | null>(null)
  const [score, setScore] = useState(0)

  if (rounds.length === 0) return <p className="muted">{g.guessLocked}</p>

  const restart = () => { setSeed((s) => s + 1); setIndex(0); setPicked(null); setScore(0) }
  if (index >= rounds.length) {
    return (
      <div className="panel game-done" aria-live="polite">
        <div>
          <p className="eyebrow accent">{g.yourScore}</p>
          <p className="display num">{score}/{rounds.length}</p>
          <p className="lede muted">{VERDICTS[Math.min(score, VERDICTS.length - 1)]}</p>
        </div>
        <div className="stack">
          <ShareButtons path={`${scenePath(scene)}#guess`} text={g.shareGuess(score, rounds.length)} />
          <button type="button" onClick={restart} className="btn btn-ghost">{g.playAgain}</button>
        </div>
      </div>
    )
  }

  const round = rounds[index]
  const answer = (model: string) => {
    if (picked) return
    setPicked(model)
    if (model === round.result.model) setScore((s) => s + 1)
  }
  return (
    <div className="game-grid">
      <Plate><PelicanMedia result={round.result} eager /></Plate>
      <div className="stack">
        <p className="eyebrow muted">{g.round(index + 1, rounds.length, score)}</p>
        <p className="card-title">{g.whichModel}</p>
        <div className="options" role="group" aria-label={g.pickModel}>
          {round.options.map((model) => {
            const state = !picked ? undefined : model === round.result.model ? 'right' : model === picked ? 'wrong' : undefined
            return (
              <button key={model} type="button" onClick={() => answer(model)} disabled={Boolean(picked)} data-state={state} className="option">
                {modelLabel(model)}
                {state === 'right' ? <span className="eyebrow">{g.correct}</span> : state === 'wrong' ? <span className="eyebrow">{g.nope}</span> : null}
              </button>
            )
          })}
        </div>
        <p aria-live="polite" className="small muted min-h">
          {picked ? <>{picked === round.result.model ? g.nice : g.itWas(modelLabel(round.result.model))} {round.result.comment ? g.judgeQuote(round.result.comment) : ''}</> : null}
        </p>
        {picked ? <button type="button" onClick={() => { setIndex((i) => i + 1); setPicked(null) }} className="btn btn-ink self-start">{index + 1 === rounds.length ? g.seeScore : g.nextPelican}</button> : null}
      </div>
    </div>
  )
}

// ─── Face-off ────────────────────────────────────────────────

function bestOf(pool: LabResult[], model: string, kind: string): LabResult | undefined {
  return pool.filter((r) => r.model === model && r.kind === kind).sort((a, b) => (b.score ?? -1) - (a.score ?? -1))[0]
}

/** Two models side by side. The pair lives in the URL (?a=&b=) so it can be shared. */
export function FaceOff({ rows, pool, scene = 'pelican-test' }: { rows: LabLeaderRow[]; pool: LabResult[]; scene?: SceneSlug }) {
  const t = useT()
  const g = t.games
  const [params, setParams] = useSearchParams()
  const bySlug = useMemo(() => new Map(rows.map((r) => [modelSlug(r.model), r.model])), [rows])
  const a = bySlug.get(params.get('a') ?? '') ?? rows[0]?.model ?? ''
  const b = bySlug.get(params.get('b') ?? '') ?? rows[1]?.model ?? ''
  const set = (key: 'a' | 'b', model: string) => {
    const next = new URLSearchParams(params)
    next.set(key, modelSlug(model))
    next.set(key === 'a' ? 'b' : 'a', modelSlug(key === 'a' ? b : a))
    setParams(next, { replace: true, preventScrollReset: true })
  }

  if (rows.length < 2) return <p className="muted">{g.faceOffLocked}</p>
  const rowOf = (m: string) => rows.find((r) => r.model === m)
  const ra = rowOf(a)
  const rb = rowOf(b)
  const winner = ra?.score != null && rb?.score != null && ra.score !== rb.score ? (ra.score > rb.score ? a : b) : null

  const side = (key: 'a' | 'b', model: string, label: string, row?: LabLeaderRow) => (
    <div className="stack">
      <label className="stack-sm">
        <span className="eyebrow muted">{label}</span>
        <select className="field" value={model} onChange={(e) => set(key, e.target.value)}>
          {rows.map((r) => <option key={r.model} value={r.model}>{modelLabel(r.model)}</option>)}
        </select>
      </label>
      {(['static', 'animated'] as const).map((kind) => {
        const r = bestOf(pool, model, kind)
        return r
          ? <Link key={kind} to={resultPath(r.id, scene)} aria-label={g.openSide(modelLabel(model), t.common.kind(kind))}><Plate><PelicanMedia result={r} eager /></Plate></Link>
          : <div key={kind} className="empty-plate eyebrow muted">{g.noKindYet(kind === 'animated')}</div>
      })}
      <dl className="facts facts-3">
        {[[t.profile.overall, formatScore(row?.score)], [g.draws, row ? `${Math.round(row.ok_rate * 100)}%` : '—'], [t.profile.avgCost, formatCost(row?.avg_cost)]].map(([k, v]) => (
          <div key={k}><dt className="eyebrow muted">{k}</dt><dd className="strong num">{v}</dd></div>
        ))}
      </dl>
      {winner === model ? <p className="pill self-start">{g.winner}</p> : null}
    </div>
  )
  const text = winner ? g.beats(winner, winner === a ? b : a) : g.versus(a, b)
  return (
    <div className="stack">
      <div className="faceoff">
        {side('a', a, g.leftCorner, ra)}
        <p className="vs" aria-hidden="true">vs</p>
        {side('b', b, g.rightCorner, rb)}
      </div>
      <ComparisonVideo pool={pool} leftModel={a} rightModel={b} winner={winner} />
      <ShareButtons path={`${scenePath(scene)}?a=${modelSlug(a)}&b=${modelSlug(b)}#face-off`} text={text} />
    </div>
  )
}

// ─── Explore ─────────────────────────────────────────────────

type View = 'model' | 'author' | 'all'
type Sort = 'score' | 'flop' | 'new' | 'cheap'
const PAGE = 24

/** Browse every public pelican: by model (all testers), by tester (all models), or everything. */
export function Explore({ models, authors }: { models: LabLeaderRow[]; authors: { name: string; count: number }[] }) {
  const t = useT()
  const bt = t.browse
  const [view, setView] = useState<View>('model')
  const [pick, setPick] = useState<string | null>(null)
  const [kind, setKind] = useState<'' | 'static' | 'animated'>('')
  const [sort, setSort] = useState<Sort>('score')
  const [items, setItems] = useState<LabResult[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  const query = useMemo(() => {
    const p = new URLSearchParams({ sort, limit: String(PAGE) })
    if (kind) p.set('kind', kind)
    if (view === 'model' && pick) p.set('model', pick)
    if (view === 'author' && pick) p.set('author', pick)
    return p.toString()
  }, [view, pick, kind, sort])

  const load = async (offset: number) => {
    setLoading(true)
    setFailed(false)
    try {
      const p = new URLSearchParams(query)
      p.set('offset', String(offset))
      const data = await labApi<{ items: LabResult[]; total: number }>(`/lab/api/pelican-test/results?${p}`)
      setItems((prev) => (offset === 0 ? data.items : [...prev, ...data.items]))
      setTotal(data.total)
    } catch (e) {
      if (!(e instanceof LabApiError)) throw e
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { void load(0) }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  const chips = view === 'model' ? models.map((m) => ({ key: m.model, label: modelLabel(m.model), count: m.results }))
    : view === 'author' ? authors.map((a) => ({ key: a.name, label: a.name, count: a.count })) : []

  return (
    <div>
      <div className="row-between wrap">
        <div className="segmented" role="group" aria-label={bt.byAria}>
          {(['model', 'author', 'all'] as View[]).map((v) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => { setView(v); setPick(null) }}>
              {v === 'model' ? bt.tabByModel : v === 'author' ? bt.tabByTester : bt.tabEverything}
            </button>
          ))}
        </div>
        <div className="row">
          <label className="sr-only" htmlFor="lab-kind">{bt.typeAria}</label>
          <select id="lab-kind" className="field field-inline" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="">{bt.allTypes}</option><option value="static">{bt.classic}</option><option value="animated">{bt.animated}</option>
          </select>
          <label className="sr-only" htmlFor="lab-sort">{bt.sort}</label>
          <select id="lab-sort" className="field field-inline" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="score">{bt.bestFirst}</option><option value="flop">{bt.wildestFirst}</option><option value="new">{bt.newest}</option><option value="cheap">{bt.cheapest}</option>
          </select>
        </div>
      </div>
      {chips.length ? (
        <div className="chips chips-filter" role="group" aria-label={view === 'model' ? bt.models : bt.testers}>
          <button type="button" aria-pressed={pick == null} onClick={() => setPick(null)}>{bt.all}</button>
          {chips.map((c) => (
            <button key={c.key} type="button" aria-pressed={pick === c.key} onClick={() => setPick(c.key)}>{c.label} <span className="subtle">{c.count}</span></button>
          ))}
        </div>
      ) : null}
      <p className="eyebrow muted explore-count" aria-live="polite">{loading && items.length === 0 ? bt.loadingPelicans : bt.pelicanCount(total)}</p>
      <div className="grid-cards">{items.map((r) => <ResultCard key={r.id} result={r} />)}</div>
      {failed ? <p className="muted">{bt.couldNotLoadPelicans} <button type="button" className="link-btn" onClick={() => load(0)}>{t.common.tryAgain}</button></p> : null}
      {!loading && !failed && total === 0 ? <p className="muted">{bt.noPelicans}</p> : null}
      {items.length < total ? (
        <div className="center pad-top">
          <button type="button" disabled={loading} onClick={() => load(items.length)} className="btn btn-ghost">{loading ? t.common.loading : bt.showMorePelicans}</button>
        </div>
      ) : null}
    </div>
  )
}
